import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  DeleteOutlined,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  UnfoldLess,
} from '@mui/icons-material';
import React from 'react';
import { Palette } from 'Palette';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import type { SaveStatus } from '../admin/newContentManagement/newContentManagementEditorTypes';
import { StyledButton } from '../styles/StyledButtons';
import FundUpdateTitleField from './FundUpdateTitleField';
import { formatFundUpdateDate, todayDateString } from './fundUpdatesAdminUtils';
import type { useFundUpdatesAdmin } from './useFundUpdatesAdmin';

type HeaderProps = {
  controller: ReturnType<typeof useFundUpdatesAdmin>;
  update: AdminFundUpdateEntry;
  open: boolean;
  onOpen: (id: string | null) => void;
  saveNow?: () => void;
  saveStatus?: SaveStatus;
  /** Lets a read-only list row fold its content away; omitted where it can't (drafts, the open editor). */
  collapse?: { expanded: boolean; onToggle: () => void };
};

function publishUpdate(controller: HeaderProps['controller'], update: AdminFundUpdateEntry) {
  return update.status === 'draft'
    ? controller.handlePublishDraft(update)
    : controller.handlePublishEditedUpdate(update);
}

function DeleteUpdateButton({
  disabled,
  onDelete,
  title,
}: {
  disabled: boolean;
  onDelete: () => void;
  title: string;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onDelete}
          sx={{ color: Palette.warning.buttonBg }}
        >
          <DeleteOutlined fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

function ModeToggleButton({
  open,
  onOpen,
  id,
}: Pick<HeaderProps, 'open' | 'onOpen'> & { id: string }) {
  return (
    <Tooltip title={open ? 'Compress' : 'Edit'}>
      <IconButton size="small" onClick={() => onOpen(open ? null : id)}>
        {open ? <UnfoldLess fontSize="small" /> : <EditOutlined fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

/** Copies a published update into a new draft, then opens that draft. */
function DuplicateUpdateButton({
  controller,
  update,
  onOpen,
}: Pick<HeaderProps, 'controller' | 'update' | 'onOpen'>) {
  const duplicating = controller.state.duplicatingIds.has(update.externalId);
  return (
    <StyledButton
      compact
      noTopSpacing
      disabled={duplicating}
      onClick={async () => {
        const duplicate = await controller.handleDuplicateUpdate(update);
        if (duplicate) onOpen(duplicate.externalId);
      }}
    >
      {duplicating ? 'Duplicating...' : 'Duplicate'}
    </StyledButton>
  );
}

function UpdateHeaderActions({
  controller,
  update,
  open,
  onOpen,
  saveNow,
  saveStatus,
}: HeaderProps) {
  const { state } = controller;
  const id = update.externalId;
  const isDraft = update.status === 'draft';
  const publishDisabled =
    state.cmsBusyIds.has(id) ||
    state.highlightGeneratingIds.has(id) ||
    state.highlightSuggestion?.id === id ||
    state.publishing;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {open && (
        <StyledButton
          compact
          noTopSpacing
          disabled={saveStatus !== 'pending' && saveStatus !== 'error'}
          onClick={saveNow}
        >
          Save
        </StyledButton>
      )}
      {(isDraft || update.hasPendingDraft) && (
        <StyledButton
          compact
          noTopSpacing
          disabled={publishDisabled || update.content.length === 0}
          onClick={() => publishUpdate(controller, update)}
        >
          Publish
        </StyledButton>
      )}
      {!isDraft && (
        <DuplicateUpdateButton controller={controller} update={update} onOpen={onOpen} />
      )}
      <ModeToggleButton open={open} onOpen={onOpen} id={id} />
      <DeleteUpdateButton
        disabled={state.discarding}
        onDelete={() =>
          isDraft ? controller.handleDeleteDraft(update) : controller.handleDeletePublished(update)
        }
        title={isDraft ? 'Delete draft' : 'Delete update'}
      />
    </Box>
  );
}

/** Shared date, title and actions for the open editor and collapsed update rows. */
export default function FundUpdateHeaderBar(props: HeaderProps) {
  const { update } = props;
  const isDraft = update.status === 'draft';
  const displayDate = isDraft
    ? formatFundUpdateDate(todayDateString())
    : formatFundUpdateDate(update.updateDate);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        bgcolor: Palette.default.background,
        borderBottom: `1px solid ${Palette.default.border}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        {props.collapse && (
          <Tooltip title={props.collapse.expanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size="small"
              aria-label={props.collapse.expanded ? 'Collapse update' : 'Expand update'}
              aria-expanded={props.collapse.expanded}
              onClick={props.collapse.onToggle}
              sx={{ flexShrink: 0, ml: -0.5 }}
            >
              {props.collapse.expanded ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="body2" sx={{ fontStyle: 'italic', flexShrink: 0 }}>
          {displayDate}
        </Typography>
        <FundUpdateTitleField
          title={update.title}
          onChange={(title) => void props.controller.handleTitleChange(update, title)}
        />
        {!isDraft && update.hasPendingDraft && (
          <Chip
            label="Unpublished changes"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
      </Box>
      <UpdateHeaderActions {...props} />
    </Box>
  );
}
